/**
 * The encoding rules a transfer syntax selects, per PS3.5 section 10 and
 * PS3.6 Annex A.
 */
export interface TransferSyntax {
    readonly uid: string;
    /** Whether elements carry their VR on the wire. */
    readonly explicitVr: boolean;
    readonly littleEndian: boolean;
    /** Whether PixelData is encapsulated in fragments rather than native. */
    readonly encapsulated: boolean;
    /** Whether the dataset following the file meta group is deflated. */
    readonly deflated: boolean;
}

export const IMPLICIT_VR_LITTLE_ENDIAN = '1.2.840.10008.1.2';
export const EXPLICIT_VR_LITTLE_ENDIAN = '1.2.840.10008.1.2.1';
export const DEFLATED_EXPLICIT_VR_LITTLE_ENDIAN = '1.2.840.10008.1.2.1.99';
/**
 * Retired in PS3.5. Recognised so a file carrying it is diagnosed by name
 * rather than failing obscurely, but refused by the reader: value bytes are
 * decoded little-endian throughout, so a big-endian dataset would yield
 * reversed numbers rather than an error.
 */
export const EXPLICIT_VR_BIG_ENDIAN = '1.2.840.10008.1.2.2';

/**
 * Resolves a transfer syntax UID to its encoding rules.
 *
 * Anything outside the three uncompressed syntaxes is an encapsulated
 * (compressed) syntax: PS3.5 requires all of those to use explicit VR little
 * endian for the dataset itself, so only PixelData's framing differs. This
 * means a transfer syntax the engine has never heard of still parses
 * correctly, with its pixel data left as opaque fragments for a decoder that
 * does know it.
 */
export function transferSyntaxFor(uid: string): TransferSyntax {
    switch (uid) {
        case IMPLICIT_VR_LITTLE_ENDIAN:
            return {
                uid,
                explicitVr: false,
                littleEndian: true,
                encapsulated: false,
                deflated: false,
            };
        case EXPLICIT_VR_LITTLE_ENDIAN:
            return {
                uid,
                explicitVr: true,
                littleEndian: true,
                encapsulated: false,
                deflated: false,
            };
        case DEFLATED_EXPLICIT_VR_LITTLE_ENDIAN:
            return {
                uid,
                explicitVr: true,
                littleEndian: true,
                encapsulated: false,
                deflated: true,
            };
        case EXPLICIT_VR_BIG_ENDIAN:
            return {
                uid,
                explicitVr: true,
                littleEndian: false,
                encapsulated: false,
                deflated: false,
            };
        default:
            return {
                uid,
                explicitVr: true,
                littleEndian: true,
                encapsulated: true,
                deflated: false,
            };
    }
}

/** The file meta group is always explicit VR little endian, per PS3.10. */
export const FILE_META_TRANSFER_SYNTAX: TransferSyntax = transferSyntaxFor(
    EXPLICIT_VR_LITTLE_ENDIAN
);
